import React, { useState, useEffect, useCallback } from 'react';
import { 
  Card, 
  Row, 
  Col, 
  Typography, 
  Avatar, 
  Button, 
  Table, 
  Tag, 
  Space, 
  Select, 
  Switch, 
  Statistic, 
  Divider,
  Alert,
  Spin,
  message,
  Tooltip,
  Radio,
  Modal
} from 'antd';
import { 
  UserOutlined, 
  CalendarOutlined, 
  DollarOutlined, 
  CheckCircleOutlined, 
  CloseCircleOutlined,
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
  DownloadOutlined,
  FileTextOutlined,
  FolderOutlined,
  PaperClipOutlined,
  StarFilled,
  MailOutlined,
  CrownOutlined
} from '@ant-design/icons';
import { useCognitoAuth } from '../contexts/CognitoAuthContext';
import apiService from '../services/api';
import SolutioLoader from './SolutioLoader';
import CubeAnimation from './CubeAnimation';
import DEPLOYMENT_CONFIG from '../config/deployment';
import dayjs from 'dayjs';
import LeadDetailsModal from './LeadDetailsModal';
import LeadDocumentsModal from './LeadDocumentsModal';
import NotesPanel from './NotesPanel';
import TaskMaster from './TaskMaster';
import EmailModal from './EmailModal';
import CustomToggle from './CustomToggle';
import PaymentSwitch from './PaymentSwitch';
import { isAdminOrDCOrTeamLeader } from '../utils/roleUtils';

const { Title, Text } = Typography;
const { Option } = Select;

interface User {
  id: number;
  username: string;
  email: string;
  avatar_url?: string;
  role: string;
  business_area: string;
  is_active: boolean;
}

interface Lead {
  id: number;
  client_name: string;
  client_phone: string;
  client_email: string;
  product_type: string;
  additional_cards: string;
  installation_date: string;
  status: string;
  notes?: string;
  payment_made?: boolean;
}

interface UserIncomeData {
  total_income: number;
  total_points: number;
  bracket_bonus: number;
  total_sales: number;
  product_breakdown: Record<string, number>;
  current_bracket: any;
}

const Solutio: React.FC = () => {
  const { user } = useCognitoAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedUser, setExpandedUser] = useState<number | null>(null);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [userLeads, setUserLeads] = useState<Lead[]>([]);
  const [userIncome, setUserIncome] = useState<UserIncomeData | null>(null);
  const [sectionLoading, setSectionLoading] = useState(false);
  const [accountingResolved, setAccountingResolved] = useState<Record<string, boolean>>({});
  const [paymentStatus, setPaymentStatus] = useState<Record<number, boolean>>({});
  const [bonusPaymentStatus, setBonusPaymentStatus] = useState<Record<string, boolean>>({});
  
  // Modal state variables
  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [selectedLead, setSelectedLead] = useState<any>(null);
  const [documentsModalVisible, setDocumentsModalVisible] = useState(false);
  const [selectedLeadForDocuments, setSelectedLeadForDocuments] = useState<any>(null);
  const [notesModalVisible, setNotesModalVisible] = useState(false);
  const [selectedLeadForNotes, setSelectedLeadForNotes] = useState<any>(null);
  const [taskModalVisible, setTaskModalVisible] = useState(false);
  const [selectedLeadForTask, setSelectedLeadForTask] = useState<any>(null);
  const [emailModalVisible, setEmailModalVisible] = useState(false);
  const [selectedLeadForEmail, setSelectedLeadForEmail] = useState<any>(null);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUserLeads = useCallback(async (userId: number) => {
    try {
      const response = await apiService.getUserLeads(userId, selectedYear, selectedMonth);
      
      if (response.success) {
        setUserLeads(response.data);
      }
    } catch (error) {
    }
  }, [selectedYear, selectedMonth]);

  const loadUserIncome = useCallback(async (userId: number) => {
    try {
      const response = await apiService.getUserIncome(userId, selectedYear, selectedMonth);
      if (response.success) {
        setUserIncome(response.data);
      }
    } catch (error) {
    }
  }, [selectedYear, selectedMonth]);

  const loadUserData = useCallback(async (userId: number) => {
    try {
      setSectionLoading(true);
      await Promise.all([
        loadUserLeads(userId),
        loadUserIncome(userId)
      ]);
    } catch (error) {
      message.error('Erro ao carregar dados do utilizador');
    } finally {
      setSectionLoading(false);
    }
  }, [loadUserLeads, loadUserIncome, selectedYear, selectedMonth]);

  // Reload user data when filters change
  useEffect(() => {
    if (expandedUser) {
      loadUserData(expandedUser);
    }
  }, [selectedMonth, selectedYear, expandedUser, loadUserData]);

  // Load accounting state from localStorage on component mount and when month/year changes
  useEffect(() => {
    const savedData = JSON.parse(localStorage.getItem('accountingResolved') || '{}');
    setAccountingResolved(savedData);
  }, [selectedMonth, selectedYear]);

  // Load payment status from localStorage on component mount and when month/year changes
  useEffect(() => {
    const savedPaymentData = JSON.parse(localStorage.getItem('paymentStatus') || '{}');
    setPaymentStatus(savedPaymentData);
  }, [selectedMonth, selectedYear]);

  // Load bonus payment status from localStorage on component mount and when month/year changes
  useEffect(() => {
    const savedBonusPaymentData = JSON.parse(localStorage.getItem('bonusPaymentStatus') || '{}');
    setBonusPaymentStatus(savedBonusPaymentData);
  }, [selectedMonth, selectedYear]);

  // Normalize role names to handle case variations and different formats
  const normalizeRole = (role: string): string => {
    const normalized = role.toLowerCase().trim();
    
    // Map different variations to standard role names
    const roleMapping: Record<string, string> = {
      'team_leader': 'líder',
      'team leader': 'líder',
      'líder': 'líder',
      'lider': 'líder',
      'vendedor': 'vendedor',
      'seller': 'vendedor',
      'gestor': 'gestor',
      'gestor comercial': 'gestor comercial',
      'manager': 'gestor',
      'consultor': 'consultor',
      'consultant': 'consultor',
      'técnico': 'técnico',
      'technician': 'técnico',
      'tecnico': 'técnico',
      'admin': 'admin',
      'administrator': 'admin'
    };
    
    return roleMapping[normalized] || normalized;
  };

  // Group users by role
  const groupUsersByRole = (users: User[]) => {
    return users.reduce((groups: Record<string, User[]>, user) => {
      const normalizedRole = normalizeRole(user.role);
      if (!groups[normalizedRole]) {
        groups[normalizedRole] = [];
      }
      groups[normalizedRole].push(user);
      return groups;
    }, {});
  };

  // Get role display name and color
  const getRoleInfo = (role: string) => {
    const roleMap: Record<string, { name: string; color: string }> = {
      'líder': { name: 'Líderes de Equipa', color: '#1890ff' },
      'vendedor': { name: 'Vendedores', color: '#52c41a' },
      'gestor': { name: 'Gestores', color: '#fa8c16' },
      'gestor comercial': { name: 'Gestores Comerciais', color: '#fa8c16' },
      'consultor': { name: 'Consultores', color: '#722ed1' },
      'técnico': { name: 'Técnicos', color: '#13c2c2' },
      'admin': { name: 'Administradores', color: '#f5222d' }
    };
    return roleMap[role] || { name: role, color: '#8c8c8c' };
  };

  // Check if user is admin
  if (!user || user.role !== 'Admin') {
    return (
      <div style={{ padding: '24px', textAlign: 'center' }}>
        <Alert
          message="Acesso Negado"
          description="Esta página é apenas para administradores."
          type="error"
          showIcon
        />
      </div>
    );
  }

  // Generate month options
  const monthOptions = [
    { value: 1, label: 'Janeiro' },
    { value: 2, label: 'Fevereiro' },
    { value: 3, label: 'Março' },
    { value: 4, label: 'Abril' },
    { value: 5, label: 'Maio' },
    { value: 6, label: 'Junho' },
    { value: 7, label: 'Julho' },
    { value: 8, label: 'Agosto' },
    { value: 9, label: 'Setembro' },
    { value: 10, label: 'Outubro' },
    { value: 11, label: 'Novembro' },
    { value: 12, label: 'Dezembro' }
  ];

  // Generate year options (current year and previous 2 years)
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 3 }, (_, i) => ({
    value: currentYear - i,
    label: (currentYear - i).toString()
  }));

  const loadUsers = async () => {
    try {
      setLoading(true);
      const response = await apiService.getUsersForSolutio();
      
      if (response.success) {
        // Handle both response structures: {data: [...]} and {users: [...]}
        const usersArray = response.data || response.users;
        if (usersArray && Array.isArray(usersArray)) {
          // Filter only active users (unblocked) and exclude Admin users
          const filteredUsers = usersArray.filter((u: User) => u.is_active && u.role !== 'Admin');
          setUsers(filteredUsers);
        } else {
          message.error('Nenhum utilizador encontrado na resposta');
        }
      } else {
        message.error(response.message || 'Erro ao carregar utilizadores');
      }
    } catch (error) {
      message.error('Erro ao carregar utilizadores');
    } finally {
      setLoading(false);
    }
  };

  const handleUserClick = async (userId: number) => {
    if (expandedUser === userId) {
      setExpandedUser(null);
      return;
    }

    setExpandedUser(userId);
    await loadUserData(userId);
  };

  const handleMonthChange = async (month: number) => {
    setSelectedMonth(month);
    if (expandedUser) {
      await loadUserData(expandedUser);
    }
  };

  const handleYearChange = async (year: number) => {
    setSelectedYear(year);
    if (expandedUser) {
      await loadUserData(expandedUser);
    }
  };

  const handleAccountingToggle = async (userId: number, resolved: boolean) => {
    try {
      const key = `${userId}-${selectedYear}-${selectedMonth}`;
      
      // Here you would make an API call to save the accounting status
      // For now, we'll just update the local state and localStorage
      setAccountingResolved(prev => ({
        ...prev,
        [key]: resolved
      }));
      
      // Save to localStorage for persistence
      const savedData = JSON.parse(localStorage.getItem('accountingResolved') || '{}');
      savedData[key] = resolved;
      localStorage.setItem('accountingResolved', JSON.stringify(savedData));
      
      message.success(`${resolved ? 'Solutio Perfēcta marcada' : 'Contabilidade marcada como pendente'}`);
    } catch (error) {
      message.error('Erro ao atualizar estado da contabilidade');
    }
  };

  const handlePaymentToggle = async (leadId: number, paid: boolean) => {
    try {
      // Update local state
      setPaymentStatus(prev => ({
        ...prev,
        [leadId]: paid
      }));
      
      // Save to localStorage for persistence
      const savedPaymentData = JSON.parse(localStorage.getItem('paymentStatus') || '{}');
      savedPaymentData[leadId] = paid;
      localStorage.setItem('paymentStatus', JSON.stringify(savedPaymentData));
      
      message.success(`${paid ? 'Pagamento marcado como realizado' : 'Pagamento marcado como pendente'}`);
    } catch (error) {
      message.error('Erro ao atualizar estado do pagamento');
    }
  };

  const handleToggleAllPayments = async (paid: boolean) => {
    try {
      const newPaymentStatus: Record<number, boolean> = {};
      
      // Update all leads to the same payment status
      userLeads.forEach(lead => {
        newPaymentStatus[lead.id] = paid;
      });
      
      // Update local state
      setPaymentStatus(prev => ({
        ...prev,
        ...newPaymentStatus
      }));
      
      // Save to localStorage for persistence
      const savedPaymentData = JSON.parse(localStorage.getItem('paymentStatus') || '{}');
      userLeads.forEach(lead => {
        savedPaymentData[lead.id] = paid;
      });
      localStorage.setItem('paymentStatus', JSON.stringify(savedPaymentData));
      
      message.success(`${paid ? 'Todos os pagamentos marcados como realizados' : 'Todos os pagamentos marcados como pendentes'}`);
    } catch (error) {
      message.error('Erro ao atualizar estado dos pagamentos');
    }
  };

  const handleBonusPaymentToggle = async (userId: number, paid: boolean) => {
    try {
      const key = `${userId}-${selectedYear}-${selectedMonth}`;
      
      // Update local state
      setBonusPaymentStatus(prev => ({
        ...prev,
        [key]: paid
      }));
      
      // Save to localStorage for persistence
      const savedBonusPaymentData = JSON.parse(localStorage.getItem('bonusPaymentStatus') || '{}');
      savedBonusPaymentData[key] = paid;
      localStorage.setItem('bonusPaymentStatus', JSON.stringify(savedBonusPaymentData));
      
      message.success(`${paid ? 'Bónus Escalão marcado como pago' : 'Bónus Escalão marcado como pendente'}`);
    } catch (error) {
      message.error('Erro ao atualizar estado do bónus escalão');
    }
  };

  // Modal handler functions
  const handleViewLead = (lead: any) => {
    setSelectedLead(lead);
    setViewModalVisible(true);
  };

  const handleCloseViewModal = () => {
    setViewModalVisible(false);
    setSelectedLead(null);
  };

  const handleViewDocuments = (lead: any) => {
    if (!lead || !lead.id) {
      message.error('Erro: Lead inválido');
      return;
    }
    setSelectedLeadForDocuments(lead);
    setDocumentsModalVisible(true);
  };

  const handleCloseDocumentsModal = () => {
    setDocumentsModalVisible(false);
    setSelectedLeadForDocuments(null);
  };

  const handleOpenNotes = (lead: any) => {
    setSelectedLeadForNotes(lead);
    setNotesModalVisible(true);
  };

  const handleCloseNotesModal = () => {
    setNotesModalVisible(false);
    setSelectedLeadForNotes(null);
  };

  const handleOpenTaskModal = (lead: any) => {
    setSelectedLeadForTask(lead);
    setTaskModalVisible(true);
  };

  const handleCloseTaskModal = () => {
    setTaskModalVisible(false);
    setSelectedLeadForTask(null);
  };

  const handleSendEmail = (lead: any) => {
    setSelectedLeadForEmail(lead);
    setEmailModalVisible(true);
  };

  const handleCloseEmailModal = () => {
    setEmailModalVisible(false);
    setSelectedLeadForEmail(null);
  };

  const getProductTypeColor = (productType: string) => {
    const colors: Record<string, string> = {
      '2P': 'blue',
      '3P': 'green',
      '4P1': 'orange',
      '4P2': 'red'
    };
    return colors[productType] || 'default';
  };

  const getAdditionalCardsText = (additionalCards: string) => {
    try {
      const cards = JSON.parse(additionalCards);
      if (Array.isArray(cards)) {
        return `${cards.length} cartão${cards.length > 1 ? 'ões' : ''}`;
      }
      return additionalCards;
    } catch {
      return additionalCards;
    }
  };

  // Create table columns matching Client Management structure (without status column)
  const leadsColumns = [
    {
      title: 'Nome',
      dataIndex: 'name',
      key: 'name',
      render: (name: string, record: any) => {
        // Convert both to numbers for comparison
        const expandedUserId = Number(expandedUser);
        const assignedToId = Number(record.assigned_to);
        const shouldShowEquipa = expandedUserId && assignedToId && assignedToId !== expandedUserId;
        const isOwnLead = assignedToId === expandedUserId;
        
        // Find the expanded user to check their role
        const expandedUserData = users.find(u => u.id === expandedUserId);
        const isTeamLeader = expandedUserData && (
          expandedUserData.role === 'team_leader' || 
          expandedUserData.role === 'Team Leader' || 
          expandedUserData.role === 'admin' || 
          expandedUserData.role === 'Admin' || 
          expandedUserData.role === 'super_admin' || 
          expandedUserData.role === 'Diretor Comercial' || 
          expandedUserData.role === 'Gestor de Equipa'
        );
        
        return (
          <div>
            <Space>
              <span style={{ fontWeight: 500 }}>{name}</span>
              {/* Show flower icon only for Team Leader's own leads */}
              {isTeamLeader && isOwnLead && (
                <span style={{ fontSize: '14px' }}>🌸</span>
              )}
              {/* Show team indicator for team members' leads */}
              {shouldShowEquipa && (
                <Tag color="blue" style={{ fontSize: '10px', marginLeft: '8px' }}>
                  Equipa
                </Tag>
              )}
              {/* Show business area tags */}
              <Tag color="blue">Telecom</Tag>
            </Space>
          </div>
        );
      },
      sorter: (a: any, b: any) => (a.name || '').localeCompare(b.name || '')
    },
    {
      title: 'Contacto',
      dataIndex: 'phone',
      key: 'phone',
      render: (text: string, record: any) => (
        <div>
          <div style={{ fontWeight: 500 }}>
            {record.phone || '-'}
          </div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            NIF: {record.dif || '-'}
          </div>
        </div>
      )
    },
    {
      title: 'Data da Instalação',
      dataIndex: 'installation_date',
      key: 'installation_date',
      render: (date: string, record: any) => {
        if (date) {
          return (
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <CalendarOutlined style={{ marginRight: '4px', color: '#1890ff' }} />
              {dayjs(date).format('DD/MM/YYYY')}
            </div>
          );
        }
        return (
          <span style={{ color: '#999', fontStyle: 'italic' }}>Selecionar data</span>
        );
      }
    },
    {
      title: 'Documentos',
      key: 'view_documents',
      render: (text: string, record: any) => (
        <Button 
          type="default" 
          size="small" 
          icon={<FileTextOutlined />}
          onClick={() => handleViewDocuments(record)}
          style={{ fontSize: '11px', height: '24px' }}
        >
          Documentos
        </Button>
      )
    },
    {
      title: 'Tipologia do Pacote',
      dataIndex: 'product_type',
      key: 'product_type',
      render: (productType: string, record: any) => {
        const productOptions = [
          { value: '2P', label: '2P', color: '#52c41a', bgColor: '#f6ffed' },
          { value: '3P', label: '3P', color: '#1890ff', bgColor: '#e6f7ff' },
          { value: '4P1', label: '4P1', color: '#722ed1', bgColor: '#f9f0ff' },
          { value: '4P2', label: '4P2', color: '#fa8c16', bgColor: '#fff7e6' }
        ];
        
        const selectedOption = productOptions.find(opt => opt.value === productType);
        
        return (
          <Radio.Group 
            value={productType} 
            size="small"
            style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}
          >
            {productOptions.map(option => (
              <Radio 
                key={option.value} 
                value={option.value}
                style={{ 
                  fontSize: '11px',
                  color: option.color,
                  backgroundColor: option.bgColor,
                  padding: '2px 6px',
                  borderRadius: '4px',
                  margin: '0'
                }}
              >
                {option.label}
              </Radio>
            ))}
          </Radio.Group>
        );
      }
    },
    {
      title: 'Cartões adicionais',
      dataIndex: 'additional_cards',
      key: 'additional_cards',
      render: (additionalCards: string, record: any) => {
        const cardOptions = [
          { value: '1', label: '1', color: '#13c2c2', bgColor: '#e6fffb', icon: '💳' },
          { value: '2', label: '2', color: '#722ed1', bgColor: '#f9f0ff', icon: '💳💳' },
          { value: '3', label: '3', color: '#fa8c16', bgColor: '#fff7e6', icon: '💳💳💳' }
        ];
        
        const selectedOption = cardOptions.find(opt => opt.value === additionalCards);
        
        return (
          <Radio.Group 
            value={additionalCards} 
            size="small"
            style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}
          >
            {cardOptions.map(option => (
              <Radio 
                key={option.value} 
                value={option.value}
                style={{ 
                  fontSize: '11px',
                  color: option.color,
                  backgroundColor: option.bgColor,
                  padding: '2px 6px',
                  borderRadius: '4px',
                  margin: '0'
                }}
              >
                {option.label}
              </Radio>
            ))}
          </Radio.Group>
        );
      }
    },
    {
      title: 'Pagamento',
      key: 'payment',
      render: (text: string, record: any) => (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <PaymentSwitch
            checked={paymentStatus[record.id] || false}
            onChange={(checked) => handlePaymentToggle(record.id, checked)}
            leadId={record.id}
          />
        </div>
      )
    },
    {
      title: 'Ações',
      key: 'actions',
      render: (text: string, record: any) => (
        <Space>
          <Tooltip title="Ver detalhes">
            <Button 
              type="text" 
              icon={<EyeOutlined />} 
              size="small"
              onClick={() => handleViewLead(record)}
            />
          </Tooltip>
          <Tooltip title="Notas">
            <Button 
              type="primary" 
              icon={<PaperClipOutlined />} 
              size="small"
              onClick={() => handleOpenNotes(record)}
              style={{ fontSize: '11px', height: '24px' }}
            />
          </Tooltip>
          <Tooltip title="Criar Tarefa">
            <Button 
              type="text" 
              icon={<CrownOutlined />} 
              size="small"
              onClick={() => handleOpenTaskModal(record)}
              style={{ color: '#faad14' }}
            />
          </Tooltip>
          {user?.role && isAdminOrDCOrTeamLeader(user.role) && (
            <Tooltip title="Enviar Email">
              <Button 
                type="text" 
                icon={<MailOutlined />} 
                size="small"
                onClick={() => handleSendEmail(record)}
                style={{ color: '#1890ff' }}
              />
            </Tooltip>
          )}
        </Space>
      )
    }
  ];

  if (loading) {
    return (
      <div style={{ 
        padding: '16px', 
        minHeight: '100vh',
        width: '90%',
        margin: '0 auto',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{ 
          textAlign: 'center',
          background: 'rgba(255, 255, 255, 0.95)',
          borderRadius: '20px',
          padding: '48px',
          boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <SolutioLoader />
        </div>
      </div>
    );
  }

  return (
    <div className="solutio-page" style={{ 
      padding: '24px', 
      position: 'relative'
    }}>
      
        {/* Floating Background Shapes */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 1,
          overflow: 'hidden',
          pointerEvents: 'none'
        }}>
          <div style={{
            position: 'absolute',
            width: '150px',
            height: '150px',
            borderRadius: '50%',
            background: 'rgba(255, 255, 255, 0.1)',
            top: '5%',
            left: '5%',
            animation: 'solutioFloat 6s ease-in-out infinite',
            animationDelay: '0s'
          }} />
          <div style={{
            position: 'absolute',
            width: '220px',
            height: '220px',
            borderRadius: '50%',
            background: 'rgba(255, 255, 255, 0.1)',
            top: '12%',
            right: '8%',
            animation: 'solutioFloat 6s ease-in-out infinite',
            animationDelay: '2s'
          }} />
          <div style={{
            position: 'absolute',
            width: '110px',
            height: '110px',
            borderRadius: '50%',
            background: 'rgba(255, 255, 255, 0.1)',
            bottom: '12%',
            left: '12%',
            animation: 'solutioFloat 6s ease-in-out infinite',
            animationDelay: '4s'
          }} />
          <div style={{
            position: 'absolute',
            width: '180px',
            height: '180px',
            borderRadius: '50%',
            background: 'rgba(255, 255, 255, 0.1)',
            bottom: '5%',
            right: '5%',
            animation: 'solutioFloat 6s ease-in-out infinite',
            animationDelay: '1s'
          }} />
          <div style={{
            position: 'absolute',
            width: '90px',
            height: '90px',
            borderRadius: '50%',
            background: 'rgba(255, 255, 255, 0.1)',
            top: '45%',
            left: '3%',
            animation: 'solutioFloat 6s ease-in-out infinite',
            animationDelay: '3s'
          }} />
          <div style={{
            position: 'absolute',
            width: '130px',
            height: '130px',
            borderRadius: '50%',
            background: 'rgba(255, 255, 255, 0.1)',
            top: '55%',
            right: '3%',
            animation: 'solutioFloat 6s ease-in-out infinite',
            animationDelay: '5s'
          }} />
          <div style={{
            position: 'absolute',
            width: '70px',
            height: '70px',
            borderRadius: '50%',
            background: 'rgba(255, 255, 255, 0.1)',
            top: '25%',
            left: '25%',
            animation: 'solutioFloat 6s ease-in-out infinite',
            animationDelay: '1.5s'
          }} />
          <div style={{
            position: 'absolute',
            width: '100px',
            height: '100px',
            borderRadius: '50%',
            background: 'rgba(255, 255, 255, 0.1)',
            top: '35%',
            right: '25%',
            animation: 'solutioFloat 6s ease-in-out infinite',
            animationDelay: '3.5s'
          }} />
          
          {/* 3D Cube Animation - Right Side Center */}
          <div style={{
            position: 'absolute',
            top: '50%',
            right: '10%',
            transform: 'translateY(-50%)',
            zIndex: 2,
            pointerEvents: 'auto'
          }}>
            <CubeAnimation />
          </div>
        </div>

      <Title level={2} style={{ 
        marginBottom: '32px', 
        marginTop: '60px',
        textAlign: 'center', 
        color: 'white',
        textShadow: '0 2px 4px rgba(0,0,0,0.3)',
        position: 'relative',
        zIndex: 2,
        fontSize: '2.6rem'
      }}>
        <FolderOutlined style={{ marginRight: '8px', fontSize: '2.6rem' }} />
        Solutio - Gestão de Contabilidade
      </Title>

      <div style={{
        maxWidth: '1200px',
        margin: '0 auto',
        padding: '0 16px',
        position: 'relative',
        zIndex: 2
      }}>
        {Object.entries(groupUsersByRole(users)).map(([role, roleUsers]) => {
          const roleInfo = getRoleInfo(role);
          return (
            <div key={role} style={{ marginBottom: '40px' }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: '20px',
                padding: '12px 20px',
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                borderRadius: '12px',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.2)'
              }}>
                <div style={{
                  width: '12px',
                  height: '12px',
                  borderRadius: '50%',
                  backgroundColor: roleInfo.color,
                  marginRight: '12px',
                  boxShadow: `0 0 10px ${roleInfo.color}`
                }} />
                <Title level={4} style={{ 
                  margin: 0, 
                  color: 'white',
                  textShadow: '0 1px 2px rgba(0,0,0,0.3)'
                }}>
                  {roleInfo.name} ({roleUsers.length})
                </Title>
              </div>
              
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '24px'
              }}>
            {roleUsers.map((user) => (
              <div
                key={user.id}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  cursor: 'pointer',
                  padding: '20px',
                  borderRadius: '16px',
                  transition: 'all 0.3s ease',
                  border: expandedUser === user.id ? '2px solid #ffffff' : '1px solid rgba(255, 255, 255, 0.2)',
                  backgroundColor: expandedUser === user.id ? 'rgba(255, 255, 255, 0.95)' : 'rgba(255, 255, 255, 0.1)',
                  backdropFilter: 'blur(10px)',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                  transform: expandedUser === user.id ? 'scale(1.05)' : 'scale(1)'
                }}
                onClick={() => handleUserClick(user.id)}
              >
                    <Avatar
                      size={80}
                      src={user.avatar_url ? (user.avatar_url.startsWith('data:image/') ? user.avatar_url : `${DEPLOYMENT_CONFIG.getBackendUrl()}/${user.avatar_url}`) : undefined}
                      icon={<UserOutlined />}
                      style={{ 
                        marginBottom: '16px',
                        border: '3px solid #f3f4f6',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
                      }}
                    />
                <div style={{ textAlign: 'center' }}>
                  <div style={{ 
                    fontWeight: 'bold', 
                    fontSize: '16px',
                    marginBottom: '4px',
                    color: expandedUser === user.id ? '#1f2937' : 'white',
                    textShadow: expandedUser === user.id ? 'none' : '0 1px 2px rgba(0,0,0,0.3)'
                  }}>
                    {user.username}
                  </div>
                </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Expanded Section */}
      {expandedUser && (
        <div style={{ marginTop: '32px', maxWidth: '1200px', margin: '32px auto 0' }}>
          {(() => {
            const user = users.find(u => u.id === expandedUser);
            if (!user) return null;
            
            return (
              <Card
                style={{ marginTop: '16px' }}
                title={
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span>
                      <UserOutlined style={{ marginRight: '8px' }} />
                      Análise de {user.username}
                    </span>
                    <Button 
                      type="text" 
                      onClick={() => setExpandedUser(null)}
                      style={{ color: '#666' }}
                    >
                      ✕
                    </Button>
                  </div>
                }
              >
                {sectionLoading ? (
                  <div style={{ 
                    textAlign: 'center', 
                    padding: '40px',
                    background: 'rgba(255, 255, 255, 0.95)',
                    borderRadius: '20px',
                    boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <SolutioLoader />
                  </div>
                ) : (
                  <>
                    {/* Filters and Controls */}
                    <Row gutter={16} style={{ marginBottom: '24px' }}>
                      <Col span={8}>
                        <Text strong>Mês:</Text>
                        <Select
                          value={selectedMonth}
                          onChange={handleMonthChange}
                          style={{ width: '100%', marginTop: '4px' }}
                        >
                          {monthOptions.map(month => (
                            <Option key={month.value} value={month.value}>
                              {month.label}
                            </Option>
                          ))}
                        </Select>
                      </Col>
                      <Col span={8}>
                        <Text strong>Ano:</Text>
                        <Select
                          value={selectedYear}
                          onChange={handleYearChange}
                          style={{ width: '100%', marginTop: '4px' }}
                        >
                          {yearOptions.map(year => (
                            <Option key={year.value} value={year.value}>
                              {year.label}
                            </Option>
                          ))}
                        </Select>
                      </Col>
                      <Col span={8}>
                        <div style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          height: '100%', 
                          gap: '12px',
                          paddingTop: '4px'
                        }}>
                          <CustomToggle
                            id={`${user.id}-${selectedYear}-${selectedMonth}`}
                            checked={accountingResolved[`${user.id}-${selectedYear}-${selectedMonth}`] || false}
                            onChange={(checked) => handleAccountingToggle(user.id, checked)}
                          />
                          <Text style={{ 
                            fontSize: '14px', 
                            fontWeight: '500',
                            lineHeight: '32px',
                            margin: 0
                          }}>
                            {accountingResolved[`${user.id}-${selectedYear}-${selectedMonth}`] ? 'Solutio Perfēcta' : 'Contabilidade Pendente'}
                          </Text>
                        </div>
                      </Col>
                    </Row>

                    {/* Payment Controls */}
                    <Row gutter={16} style={{ marginBottom: '16px' }}>
                      <Col span={24}>
                        <div style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: '12px',
                          padding: '12px',
                          backgroundColor: '#f5f5f5',
                          borderRadius: '8px',
                          border: '1px solid #d9d9d9'
                        }}>
                          <Text strong style={{ fontSize: '14px' }}>
                            Controle de Pagamentos:
                          </Text>
                          <Button 
                            type="primary" 
                            size="small"
                            onClick={() => handleToggleAllPayments(true)}
                            style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
                          >
                            Marcar Todos como Pagos
                          </Button>
                          <Button 
                            type="default" 
                            size="small"
                            onClick={() => handleToggleAllPayments(false)}
                            style={{ borderColor: '#fa8c16', color: '#fa8c16' }}
                          >
                            Marcar Todos como Pendentes
                          </Button>
                          <Text style={{ fontSize: '12px', color: '#666', marginLeft: '8px' }}>
                            ({userLeads.filter(lead => paymentStatus[lead.id]).length} de {userLeads.length} pagos)
                          </Text>
                        </div>
                      </Col>
                    </Row>

                    <Divider />

                    {/* Income Calculator */}
                    {userIncome && (
                      <Card size="small" style={{ marginBottom: '24px' }}>
                        <Title level={4}>
                          <DollarOutlined style={{ marginRight: '8px' }} />
                          Calculadora de Rendimento
                        </Title>
                        <Row gutter={16}>
                          <Col span={6}>
                            <Statistic
                              title="Pagamento Pendente"
                              value={(() => {
                                const unpaidLeads = userLeads.filter(lead => !paymentStatus[lead.id]);
                                // Calculate total income for unpaid leads only
                                const unpaidRatio = unpaidLeads.length / userLeads.length;
                                const unpaidIncome = Math.round(userIncome.total_income * unpaidRatio);
                                
                                // Add bonus if it's NOT marked as paid
                                const bonusKey = `${expandedUser}-${selectedYear}-${selectedMonth}`;
                                const bonusPaid = bonusPaymentStatus[bonusKey];
                                const bonusAmount = !bonusPaid ? userIncome.bracket_bonus : 0;
                                
                                return unpaidIncome + bonusAmount;
                              })()}
                              suffix="€"
                              valueStyle={{ color: '#fa8c16', textAlign: 'right' }}
                            />
                          </Col>
                          <Col span={6}>
                            <Statistic
                              title="Pagamento Realizado"
                              value={(() => {
                                const paidLeads = userLeads.filter(lead => paymentStatus[lead.id]);
                                // Calculate total income for paid leads only
                                const paidRatio = paidLeads.length / userLeads.length;
                                const paidIncome = Math.round(userIncome.total_income * paidRatio);
                                
                                // Add bonus if it's marked as paid
                                const bonusKey = `${expandedUser}-${selectedYear}-${selectedMonth}`;
                                const bonusPaid = bonusPaymentStatus[bonusKey];
                                const bonusAmount = bonusPaid ? userIncome.bracket_bonus : 0;
                                
                                return paidIncome + bonusAmount;
                              })()}
                              suffix="€"
                              valueStyle={{ color: '#3f8600', textAlign: 'right' }}
                            />
                          </Col>
                          <Col span={6}>
                            <Statistic
                              title="Pontos Base"
                              value={userIncome.total_points}
                              valueStyle={{ color: '#1890ff', textAlign: 'right' }}
                            />
                          </Col>
                          <Col span={6}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                              <Statistic
                                title="Bónus Escalão"
                                value={userIncome.bracket_bonus}
                                suffix="€"
                                valueStyle={{ color: '#722ed1', textAlign: 'right' }}
                              />
                              <div style={{ marginLeft: '8px' }}>
                                <PaymentSwitch
                                  checked={bonusPaymentStatus[`${expandedUser}-${selectedYear}-${selectedMonth}`] || false}
                                  onChange={(checked) => handleBonusPaymentToggle(expandedUser, checked)}
                                  leadId={expandedUser}
                                />
                              </div>
                            </div>
                          </Col>
                        </Row>
                        {userIncome.current_bracket && (
                          <div style={{ marginTop: '12px' }}>
                            <Tag color="gold">
                              Escalão Atual: {userIncome.current_bracket.name}
                            </Tag>
                          </div>
                        )}
                      </Card>
                    )}

                    {/* Leads Table */}
                    <Card size="small">
                      <Title level={4}>
                        <CalendarOutlined style={{ marginRight: '8px' }} />
                        Leads Instalados ({userLeads.length})
                      </Title>
                      <Table
                        columns={leadsColumns}
                        dataSource={userLeads}
                        rowKey="id"
                        pagination={{
                          pageSize: 10,
                          showSizeChanger: true,
                          showQuickJumper: true,
                          showTotal: (total, range) => 
                            `${range[0]}-${range[1]} de ${total} leads`
                        }}
                        scroll={{ x: 1200 }}
                      />
                    </Card>
                  </>
                )}
              </Card>
            );
          })()}
        </div>
      )}
      
      {/* Modals */}
      <LeadDetailsModal 
        open={viewModalVisible} 
        lead={selectedLead} 
        onClose={handleCloseViewModal}
      />

      {selectedLeadForDocuments?.id && (
        <LeadDocumentsModal
          open={documentsModalVisible}
          leadId={selectedLeadForDocuments.id}
          leadName={selectedLeadForDocuments.name || 'Lead'}
          onClose={handleCloseDocumentsModal}
          onRefresh={() => {
            // Refresh documents if needed
          }}
        />
      )}

      <NotesPanel
        visible={notesModalVisible}
        onCancel={handleCloseNotesModal}
        leadId={selectedLeadForNotes?.id || 0}
        notes={[]}
        onNotesChange={() => {}}
      />

      <Modal
        title="Criar Tarefa"
        open={taskModalVisible}
        onCancel={handleCloseTaskModal}
        footer={null}
        width={800}
        style={{ top: 20 }}
      >
        <TaskMaster 
          preSelectedLead={selectedLeadForTask}
          onTaskCreated={() => setTaskModalVisible(false)}
          isModal={true}
        />
      </Modal>

      <EmailModal
        visible={emailModalVisible}
        onClose={handleCloseEmailModal}
        lead={selectedLeadForEmail}
      />
    </div>
  );
};

export default Solutio;
